import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { canRead, canWrite, hasOtherModules } from '../../UI/dashboardAccess'
import { colors, fontFamily, loadFieldFont } from './fieldTheme'
import { fetchOpenTour, fetchTour, fetchTours, createTour, closeTour, reopenTour, hideTourFor, showTourFor, keepTourOpen, saveTourEntries, fetchActivity, recordActivity, removeActivity, fetchPitches, tourWithInventory } from './fieldApi'
import { tourEntry, followUpsOf } from './fieldActivity'
import FieldMyActivity from './FieldMyActivity'
import FieldActivityInventory from './FieldActivityInventory'
import { newTourAction, withActions, entriesAt, isHiddenFor, shouldRemind, tourIdFromPath, pitchLocationFromPath, pitchRecapFromPath, pitchPath, tourPath, isAdmin, canStartTour, doneCount, pitchDoneCount, stateCounts } from './fieldTours'
import { findPitchByLocation } from './fieldSearch'
import { plural } from './fieldReference'
import { startOutbox, subscribeOutbox, outboxSummary, hasPending } from './fieldOutbox'
import { nowSeconds } from './fieldDates'
import FieldTourNew from './FieldTourNew'
import FieldTourPlan from './FieldTourPlan'
import FieldTourPitch from './FieldTourPitch'
import { TourOptionsSheet, TourShareSheet } from './FieldTourOptions'
import { UndoToast } from './FieldComments'
import FieldHeader from './FieldHeader'
import FieldHome from './FieldHome'
import FieldStart from './FieldStart'
import FieldInventory from './FieldInventory'
import { Pastille, PrimaryButton, BottomBar, BottomSheet, SheetGroup, SheetRow, LoadingText } from './FieldUI'
import { HistoryIcon, TourIcon, ClearIcon } from './FieldIcons'
import './Field.css'

// Inventaires terrain : adresse plein écran /inventaires-terrain (décision 20).
// Planning : Inventory/PLANNING-INVENTAIRES-STAFF.md · Maquettes : rangée « Test inspiration Waze » du canvas.

const TOUR_URL = '/inventaires-terrain?ouvrir=tour'

function currentPath(history)
{
	if (history && history.location && history.location.pathname)
		return history.location.pathname
	return typeof window !== 'undefined' ? window.location.pathname : ''
}

const PITCH_CHECK_MS = 3000 // attente maximale de la relecture du tour à l'ouverture d'un emplacement
const REFRESH_RETRIES = 3 // relectures du tour quand une modification locale double une lecture en cours
const EMPTY_PLAN = { zone: 'all', filter: null, query: '', collapsed: true } // filtres repliés par défaut

// Lien vers un tour (décision 39) : sans droit d'écriture (W4c), ou tour introuvable
function TourLinkMessage({ title, text, onBack })
{
	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, padding: 24, textAlign: 'center' }}>
				<Pastille type="tour" size={132} illustrationSize={92} />
				<h1 style={{ fontSize: 26, lineHeight: 1.2, fontWeight: 800, color: colors.text }}>{title}</h1>
				<p style={{ fontSize: 18, fontWeight: 500, color: colors.textSecondary }}>{text}</p>
			</div>
			<BottomBar>
				<PrimaryButton onClick={onBack}>Retour à l'accueil</PrimaryButton>
			</BottomBar>
		</div>
	)
}

export default function FieldInventoriesApp({ user, history })
{
	const permissions = user ? user.permissions : null
	const readAllowed = canRead(permissions, 'inventories')
	const writeAllowed = canWrite(permissions, 'inventories')
	const showDashboard = hasOtherModules(permissions, 'inventories')

	const [tour, setTour] = useState(null)
	const [flow, setFlow] = useState(null)
	const [inventory, setInventory] = useState(null)
	const [tourScreen, setTourScreen] = useState(null)
	const [sheet, setSheet] = useState(null)
	const [toast, setToast] = useState(null)
	const [linkMessage, setLinkMessage] = useState(null)
	// Arrivée par ?ouvrir=tour (retour d'une intervention du tour) : on attend la réponse du serveur avant de
	// choisir l'écran, pour ne pas afficher l'accueil une fraction de seconde avant le plan du tour
	// Arrivée par un lien de tour (/inventaires-terrain/tour/<id>…) : même attente
	const [shortcutPending, setShortcutPending] = useState(() => {
		const search = (history && history.location && history.location.search) || (typeof window !== 'undefined' ? window.location.search : '')
		return readAllowed && writeAllowed && (new URLSearchParams(search).get('ouvrir') === 'tour' || tourIdFromPath(currentPath(history)) !== null)
	})
	const [reminded, setReminded] = useState(false)
	const [activity, setActivity] = useState(null)
	const [activityEntries, setActivityEntries] = useState([])
	const [pitches, setPitches] = useState(null)
	const [pitchesError, setPitchesError] = useState(false)
	const [closedTours, setClosedTours] = useState({})
	const [manage, setManage] = useState(null)
	const [planState, setPlanState] = useState(EMPTY_PLAN)
	const [tourPitch, setTourPitch] = useState(null)
	const [tourToast, setTourToast] = useState(null)
	const scrollRef = useRef(null)
	const planScroll = useRef(null)
	const tourRef = useRef(tour)
	tourRef.current = tour
	const pitchChecking = useRef(false) // un emplacement est en cours de vérification : les autres touchers sont ignorés
	const [pitchCheckingSlow, setPitchChecking] = useState(false)
	const tourEpoch = useRef(0) // change à chaque modification du tour faite sur ce téléphone
	const dismissTourToast = useCallback(() => setTourToast(null), [])
	const dismissToast = useCallback(() => setToast(null), [])
	const userId = user ? user._id : null

	useEffect(() => {
		loadFieldFont()
	}, [])

	// Écran fixe (retour d'Alexandra du 24/10/2026, web app iOS) : seule la zone de l'app défile,
	// la page derrière ne bouge plus (le haut ne se fait plus rogner) et prend la couleur de fond
	// (plus de barre grise sous la zone de l'indicateur d'accueil).
	useEffect(() => {
		const root = document.documentElement
		const body = document.body
		const previous = { rootBackground: root.style.background, bodyBackground: body.style.background, rootOverflow: root.style.overflow, bodyOverflow: body.style.overflow }
		root.style.background = colors.background
		body.style.background = colors.background
		root.style.overflow = 'hidden'
		body.style.overflow = 'hidden'
		return () => {
			root.style.background = previous.rootBackground
			body.style.background = previous.bodyBackground
			root.style.overflow = previous.rootOverflow
			body.style.overflow = previous.bodyOverflow
		}
	}, [])

	// Tour ouvert le plus récent ; un lien /inventaires-terrain/tour/<id> ouvre directement ce tour
	useEffect(() => {
		if (!readAllowed)
			return ;
		let cancelled = false
		const linkedId = tourIdFromPath(currentPath(history))
		const linkedLocation = pitchLocationFromPath(currentPath(history)) // adresse d'un emplacement (fiche, ou compte rendu avec /recap)
		const linkedRecap = pitchRecapFromPath(currentPath(history))
		// Raccourci de l'accueil du dashboard : ?ouvrir=tour ouvre le tour en cours, ou en crée un
		const search = (history && history.location && history.location.search) || (typeof window !== 'undefined' ? window.location.search : '')
		const openTourShortcut = new URLSearchParams(search).get('ouvrir') === 'tour'
		// Un lien de tour : le tour du lien et les emplacements sont demandés en même temps que le tour ouvert, et
		// l'écran n'est choisi qu'une fois tout reçu, en un seul rendu : ni accueil, ni plan, ni fiche de passage.
		const linked = linkedId && writeAllowed
		if (linked && linkedLocation)
			loadPitches()
		Promise.all([fetchOpenTour(), linked ? fetchTour(linkedId) : Promise.resolve(null)]).then(([openTour, linkedTour]) => {
			if (cancelled)
				return ;
			ReactDOM.unstable_batchedUpdates(() => {
				setTour(openTour)
				setShortcutPending(false)
				if (!linkedId && openTourShortcut && writeAllowed)
				{
					setTourScreen(openTour ? 'plan' : (canStartTour(user) ? 'new' : null))
					if (history && history.replace)
						history.replace('/inventaires-terrain')
				}
				else if (linkedId && !writeAllowed)
					setLinkMessage({ title: 'Vous n\'avez pas accès à ce tour', text: 'Demandez à votre responsable le droit de faire des inventaires.' })
				else if (linkedId && (!linkedTour || linkedTour.status !== 'open'))
					setLinkMessage({ title: 'Ce tour n\'est plus disponible', text: 'Il a été terminé, ou il n\'existe pas.' })
				else if (linkedId)
				{
					setTour(linkedTour)
					if (linkedLocation)
					{
						// Fiche de l'emplacement, ou son compte rendu (/recap) ; les actions déjà enregistrées sont « faites »
						setTourPitch({ pitch: { location: linkedLocation }, beforeIds: entriesAt(linkedTour, linkedLocation).map(entry => entry.id), report: linkedRecap })
						setTourScreen('pitch')
					}
					else
						setTourScreen('plan')
				}
			})
		})
		return () => {
			cancelled = true
		}
	}, [readAllowed, writeAllowed, user, history])

	// L'adresse suit l'emplacement ouvert (remplace l'entrée courante, sans en ajouter) : /inventaires-terrain/tour/<id>/<emplacement>,
	// avec /recap quand son compte rendu est ouvert.
	// En quittant la fiche, l'adresse revient à celle du plan, ou à l'accueil.
	const pitchUrlSet = useRef(false)
	useEffect(() => {
		if (!history || !history.replace || !tour || !tour._id)
			return ;
		const path = currentPath(history)
		const state = history.location ? history.location.state : undefined
		if (tourScreen === 'pitch' && tourPitch)
		{
			pitchUrlSet.current = true
			const recap = tourPitch.report === true
			if (pitchLocationFromPath(path) !== tourPitch.pitch.location || tourIdFromPath(path) !== tour._id || pitchRecapFromPath(path) !== recap)
				history.replace(pitchPath(tour, tourPitch.pitch.location, recap), state)
		}
		else if (pitchUrlSet.current)
		{
			pitchUrlSet.current = false
			if (pitchLocationFromPath(path) !== null)
				history.replace(tourScreen === 'plan' ? tourPath(tour) : '/inventaires-terrain', state)
		}
	}, [history, tour, tourScreen, tourPitch])

	const scrollToTop = useCallback(() => {
		if (scrollRef.current)
			scrollRef.current.scrollTop = 0
	}, [])

	// En revenant au plan du tour, on retrouve l'endroit où l'on était
	useEffect(() => {
		if (tourScreen === 'plan' && !inventory && planScroll.current !== null && scrollRef.current)
			scrollRef.current.scrollTop = planScroll.current
		else
			scrollToTop()
	}, [flow, inventory, tourScreen, tourPitch, activity, scrollToTop])

	// Emplacements du tour : tous ceux du paramétrage (décision 50), lus une fois par session
	const loadPitches = useCallback(() => {
		setPitchesError(false)
		fetchPitches()
			.then(setPitches)
			.catch(() => setPitchesError(true))
	}, [])

	useEffect(() => {
		if ((tourScreen || tour) && !pitches && !pitchesError)
			loadPitches()
	}, [tourScreen, tour, pitches, pitchesError, loadPitches])

	const visibleTour = tour && tour.status === 'open' && !isHiddenFor(tour, user) ? tour : null
	const hiddenTour = tour && tour.status === 'open' && isHiddenFor(tour, user) ? tour : null
	const atHome = readAllowed && !linkMessage && !inventory && !flow && !tourScreen && !activity

	// Relance des 30 jours (décision 22) : le volet s'ouvre de lui-même, une fois par ouverture de l'accueil
	useEffect(() => {
		if (atHome && !reminded && visibleTour && shouldRemind(visibleTour, user, nowSeconds()))
		{
			setReminded(true)
			setSheet('reminder')
		}
	}, [atHome, reminded, visibleTour, user])

	const goToDashboard = () => {
		if (history)
			history.push('/')
	}

	const leaveTourLink = () => {
		if (history && history.replace && tourIdFromPath(currentPath(history)))
			history.replace('/inventaires-terrain')
	}

	const goHome = () => {
		setFlow(null)
		setInventory(null)
		setTourScreen(null)
		setLinkMessage(null)
		setActivity(null)
		setTourPitch(null)
		setTourToast(null)
		planScroll.current = null
		leaveTourLink()
	}

	// « Tour de terrain » ouvre le tour en cours, ou en crée un : un seul tour à la fois (décision 50)
	const openTour = () => {
		setFlow(null)
		setInventory(null)
		setActivity(null)
		setTourPitch(null)
		setTourScreen('plan')
	}

	// Ouvrir un emplacement : le tour est d'abord relu sur le serveur, réseau d'abord, pour ne pas afficher une fiche
	// que des collègues ont déjà complétée depuis l'ouverture du plan (leurs actions comptent dans « beforeIds »).
	// Pas plus de PITCH_CHECK_MS d'attente : sans réponse (hors ligne), la fiche s'ouvre avec le tour connu.
	// check = false : retour d'un inventaire que ce téléphone vient d'enregistrer, rien à vérifier.
	const openPitch = (pitch, fromPlan, check = true) => {
		if (pitchChecking.current)
			return ;
		if (fromPlan && scrollRef.current)
			planScroll.current = scrollRef.current.scrollTop
		const known = findPitchByLocation(pitches || [], pitch.location) || pitch
		const show = (current) => {
			setTourToast(null)
			setTourPitch({ pitch: known, beforeIds: entriesAt(current, known.location).map(entry => entry.id) })
			setTourScreen('pitch')
		}
		if (!check)
		{
			show(tourRef.current)
			return ;
		}
		pitchChecking.current = true
		const slow = setTimeout(() => setPitchChecking(true), 400)
		const timeout = new Promise(resolve => setTimeout(() => resolve(null), PITCH_CHECK_MS))
		Promise.race([refreshTour(), timeout])
		.catch(() => null)
		.then(fresh => {
			clearTimeout(slow)
			pitchChecking.current = false
			setPitchChecking(false)
			const current = fresh || tourRef.current
			if (current.status !== 'open') // terminé par un collègue : refreshTour a déjà ramené à l'accueil
				return ;
			show(current)
		})
	}

	// Ce qui change dans les actions du tour est envoyé au serveur, qui les ajoute à celles des collègues
	// Une intervention du tour s'ouvre dans le module Interventions (/interventions/<id>). L'entrée actuelle de
	// l'historique devient le tour (?ouvrir=tour), la fiche vient juste après : son bouton retour, le geste ou le
	// bouton retour du navigateur ramènent au tour directement, sans repasser par la liste des interventions.
	const openIntervention = (id) => {
		if (!history || !history.push)
			return ;
		// Un emplacement est ouvert : l'entrée actuelle est déjà son adresse, c'est là qu'on revient. Sinon, le plan du tour.
		const onPitch = pitchLocationFromPath(currentPath(history)) !== null
		const back = onPitch ? currentPath(history) : TOUR_URL
		if (!onPitch && history.replace)
			history.replace(TOUR_URL)
		history.push('/interventions/' + encodeURIComponent(id), { from: back, depth: 1 })
	}

	const changeTour = (change) => {
		const previous = tourRef.current
		return updateTour(saveTourEntries(previous, change(previous)))
	}

	// Mon activité (canvas A1 à A5) : journal relu à chaque ouverture
	// Les administrateurs retrouvent les tours terminés pour pouvoir les rouvrir depuis l'activité
	const openActivity = () => Promise.all([fetchActivity(), isAdmin(user) ? fetchTours().catch(() => []) : Promise.resolve([])]).then(([list, tours]) => {
		const closed = {}
		tours.filter(item => item.status === 'closed').forEach(item => {
			closed[item._id] = item
		})
		setClosedTours(closed)
		setFlow(null)
		setInventory(null)
		setTourScreen(null)
		setActivityEntries(list)
		setActivity({ screen: 'list' })
	})

	// Options d'un tour terminé (administrateur, aucun tour en cours) : le rouvrir, ou le rouvrir et le reprendre
	// s'il reste des emplacements à faire
	const canManageEntry = (entry) => isAdmin(user) && entry.kind === 'tour' && !!closedTours[entry.tourId] && !(tour && tour.status === 'open')

	const manageTour = (entry) => {
		const closed = closedTours[entry.tourId]
		const known = pitches ? Promise.resolve(pitches) : fetchPitches().then(list => {
			setPitches(list)
			return list
		}).catch(() => null)
		setManage({ tour: closed, left: null, error: false, busy: false })
		known.then(list => setManage(current => (current && current.tour === closed ? { ...current, left: list ? stateCounts(list, closed).todo : null } : current)))
	}

	const reopenClosedTour = (resume) => {
		if (!manage || manage.busy)
			return ;
		setManage({ ...manage, busy: true, error: false })
		reopenTour(manage.tour, user)
			.then(reopened => {
				setTour(reopened)
				setManage(null)
				if (resume)
					openTour()
				else
				{
					goHome()
					setToast({ text: 'Tour réouvert' })
				}
			})
			.catch(() => setManage(current => (current ? { ...current, busy: false, error: true } : current)))
	}

	const inventoryOf = (entry) => {
		if (entry.kind === 'inventory')
			return entry.snapshot ? entry : null
		if (entry.kind === 'comment' || entry.kind === 'intervention')
			return activityEntries.find(item => item.id === entry.inventoryEntryId && item.snapshot) || null
		return null
	}

	const canOpenEntry = (entry) => !!inventoryOf(entry) || (entry.kind === 'tour' && !!tour && tour.status === 'open' && tour._id === entry.tourId)

	const openEntry = (entry) => {
		const target = inventoryOf(entry)
		if (target)
		{
			setActivity({ screen: 'inventory', entry: target })
			return ;
		}
		openTour()
	}

	// Les changements d'un tour sont faits par le serveur, qui renvoie le tour à jour
	const updateTour = (request) => request.then(saved => {
		tourEpoch.current++
		setTour(saved)
		return saved
	})

	// Plan du tour : à chaque affichage (et au retour sur l'app, un téléphone passe souvent en arrière-plan), le tour
	// est relu sur le serveur, réseau d'abord, pour voir les emplacements validés par les collègues sans recharger la
	// page. L'écran n'attend pas : il montre le tour connu, puis se met à jour. Hors ligne ou sans réponse du serveur,
	// on garde le tour connu. Une réponse partie avant une modification faite ici est écartée et le tour est relu (elle l'effacerait).
	// Résout le tour à utiliser : le plus récent connu (celui du serveur, ou le tour déjà connu sans réponse)
	const refreshTour = useCallback((attempt = 0) => {
		const current = tourRef.current
		if (!current || !current._id)
			return Promise.resolve()
		const epoch = tourEpoch.current
		return fetchTour(current._id).then(fresh => {
			if (!fresh)
				return current
			// Réponse partie avant une modification faite ici : elle est écartée et le tour est relu, la lecture
			// suivante part après l'enregistrement de cette modification et la contient.
			if (epoch !== tourEpoch.current)
				return (attempt < REFRESH_RETRIES) ? refreshTour(attempt + 1) : tourRef.current
			// Hors ligne, le service worker rend sa dernière copie du tour : plus ancienne que ce que ce téléphone sait
			// (last_activity_time ne fait qu'augmenter), elle est ignorée plutôt que d'effacer les derniers gestes.
			if ((fresh.last_activity_time || 0) < (tourRef.current.last_activity_time || 0))
				return tourRef.current
			setTour(fresh)
			if (fresh.status !== 'open')
			{
				setTourScreen(null)
				setToast({ text: 'Ce tour a été terminé.' })
			}
			return fresh
		})
	}, [])

	// Enregistrements faits sans réseau : renvoyés en arrière-plan (fieldOutbox, custom-service-worker.js). Quand la file
	// se vide, le tour est relu pour retrouver les adresses définitives des photos et les gestes des collègues.
	const refreshRef = useRef(refreshTour)
	refreshRef.current = refreshTour
	const [outbox, setOutbox] = useState(outboxSummary)
	useEffect(() => {
		let waiting = outboxSummary().pending
		const unsubscribe = subscribeOutbox(summary => {
			setOutbox(summary)
			if (waiting > 0 && summary.pending === 0)
				refreshRef.current()
			waiting = summary.pending
		})
		const stop = startOutbox()
		return () => {
			unsubscribe()
			stop()
		}
	}, [])

	useEffect(() => {
		if (tourScreen !== 'plan')
			return ;
		refreshTour()
		const onVisible = () => {
			if (document.visibilityState === 'visible')
				refreshTour()
		}
		document.addEventListener('visibilitychange', onVisible)
		return () => document.removeEventListener('visibilitychange', onVisible)
	}, [tourScreen, refreshTour])

	const start = (type) => {
		if (type !== 'tour')
			setFlow(type)
		else if (tour && tour.status === 'open')
			openTour()
		else if (canStartTour(user))
			setTourScreen('new')
	}

	const endTour = () => {
		const previous = tour
		setSheet(null)
		updateTour(closeTour(tour, user))
			.then(() => recordActivity(tourEntry({ tour: previous, action: 'end', count: pitchDoneCount(previous, pitches) != null ? pitchDoneCount(previous, pitches) : doneCount(previous), user, time: nowSeconds() })))
			.then(entry => setToast({ text: 'Tour terminé', undo: () => removeActivity(entry.id).then(() => updateTour(reopenTour(previous, user))) }))
			.catch(() => setToast({ text: 'Le tour n\'a pas pu être terminé : vérifiez la connexion.' }))
	}

	// « Reprendre » un tour masqué : il revient sur mon accueil, puis s'ouvre. Si le serveur ne répond pas,
	// le tour s'ouvre quand même (il restera masqué).
	const resumeTour = () => {
		if (hiddenTour)
			updateTour(showTourFor(hiddenTour, user)).catch(() => null)
		openTour()
	}

	const hideTour = () => {
		setSheet(null)
		updateTour(hideTourFor(tour, user)).catch(() => null)
	}

	const keepTour = () => {
		setSheet(null)
		updateTour(keepTourOpen(tour, user)).catch(() => null)
	}

	// Inventaire d'un tour enregistré : l'emplacement compte dans le tour (décision 15), avec les problèmes
	// signalés pendant l'inventaire, puis retour à sa fiche (décision 50)
	// L'inventaire et ses interventions partent avec les actions du tour (saveTourEntries) : l'écran de l'inventaire reste
	// ouvert jusqu'à l'enregistrement, ou jusqu'à la mise en file d'attente sans réseau, et garde toute la saisie en cas d'échec.
	const onTourInventorySaved = ({ payload, gaps, interventions }) => {
		const pitch = inventory.pitch
		const time = nowSeconds()
		const previous = tourRef.current
		const inventoryAction = newTourAction({ location: pitch.location, kind: 'inventory', user, now: time, inventory_id: null, gaps, pending_inventory: { payload } })
		const actions = [inventoryAction]
			.concat(interventions.map(item => newTourAction({ location: pitch.location, kind: 'intervention', user, now: time, intervention_id: null, type: item.doc.type, label: item.doc.label, description: item.doc.description, pending_intervention: { doc: item.doc, photos: item.photos, after_inventory: inventoryAction.id } })))
		return changeTour(current => withActions(current, actions, time))
			.then(() => {
				setInventory(null)
				if (!tourPitch || tourPitch.pitch.location !== pitch.location)
				{
					// la fiche s'ouvre comme avant l'inventaire : ses actions sont celles de ce passage
					const known = findPitchByLocation(pitches || [], pitch.location) || pitch
					setTourPitch({ pitch: known, beforeIds: entriesAt(previous, known.location).map(entry => entry.id) })
				}
				setTourScreen('pitch')
				setTourToast({ text: hasPending(previous._id) ? 'Inventaire de ' + pitch.location + ' enregistré sur le téléphone, envoi dès le retour du réseau.' : 'Inventaire de ' + pitch.location + ' enregistré.' })
			})
	}

	let content = null
	if (!readAllowed)
	{
		content = (
			<React.Fragment>
				<FieldHeader showDashboard={showDashboard} onDashboard={goToDashboard} />
				<p style={{ padding: '24px 20px', color: colors.text, fontSize: 17, fontWeight: 600 }}>
					Vous n'avez pas accès aux inventaires. Demandez l'accès à votre responsable.
				</p>
			</React.Fragment>
		)
	}
	else if (linkMessage)
		content = <TourLinkMessage title={linkMessage.title} text={linkMessage.text} onBack={goHome} />
	else if (shortcutPending)
		content = <LoadingText>Ouverture du tour…</LoadingText>
	else if (tourScreen === 'pitch' && tour && tourPitch && !pitches && !pitchesError)
		content = <LoadingText>Ouverture de l'emplacement…</LoadingText>
	else if (inventory)
	{
		content = (
			<FieldInventory
				key={inventory.pitch.location + '-' + (inventory.startedAt || 0)}
				context={inventory}
				user={user}
				onClose={inventory.tour && tourScreen === 'pitch' ? (result) => {
					setInventory(null)
					// « Enregistrer pour plus tard » : l'inventaire est mis de côté, pas déposé
					if (result && result.draft)
						setTourToast({ text: 'Inventaire de ' + inventory.pitch.location + ' mis de côté : à reprendre plus tard.' })
				} : goHome}
				onTourSaved={onTourInventorySaved}
				onActivity={openActivity}
				scrollToTop={scrollToTop}
			/>
		)
	}
	else if (activity && activity.screen === 'inventory')
	{
		content = (
			<FieldActivityInventory
				entry={activity.entry}
				followUps={followUpsOf(activityEntries, activity.entry)}
				user={user}
				now={nowSeconds()}
				onBack={() => openActivity()}
				onEdit={() => {
					const snap = activity.entry.snapshot
					setActivity(null)
					setInventory({ type: snap.type, pitch: snap.pitch, stay: snap.stay, client: snap.client, tour: snap.type === 'tour' && tour && tour._id === activity.entry.tourId ? tour : null, edit: { snapshot: snap, inventoryId: activity.entry.inventoryId || null }, startedAt: nowSeconds() })
				}}
			/>
		)
	}
	else if (activity)
	{
		content = (
			<React.Fragment>
				<FieldMyActivity
					user={user}
					entries={activityEntries}
					teamAllowed={isAdmin(user)}
					now={nowSeconds()}
					canOpen={canOpenEntry}
					onOpen={openEntry}
					canManage={canManageEntry}
					onManage={manageTour}
					onBack={goHome}
				/>
				{manage && (
					<BottomSheet
						title="Options du tour"
						text={manage.tour.name + (manage.left === null ? '' : (manage.left === 0 ? ', tous les emplacements sont faits' : ', ' + manage.left + (manage.left > 1 ? ' emplacements restent à faire' : ' emplacement reste à faire')))}
						onClose={() => setManage(null)}
					>
						{manage.error && <p role="alert" style={{ fontSize: 17, fontWeight: 600, color: '#7F1D1D' }}>Le tour n'a pas pu être rouvert : vérifiez la connexion et qu'aucun autre tour n'est en cours.</p>}
						<SheetGroup>
							<SheetRow label="Réouvrir le tour" icon={<HistoryIcon />} onClick={() => reopenClosedTour(false)} />
							{manage.left !== 0 && <SheetRow label="Réouvrir et reprendre le tour" icon={<TourIcon />} onClick={() => reopenClosedTour(true)} />}
						</SheetGroup>
						<SheetGroup>
							<SheetRow label="Annuler" icon={<ClearIcon />} onClick={() => setManage(null)} />
						</SheetGroup>
					</BottomSheet>
				)}
			</React.Fragment>
		)
	}
	else if (tourScreen === 'new')
	{
		content = (
			<FieldTourNew
				onBack={goHome}
				onStart={(name) => updateTour(createTour(name, user)).then(created => {
					recordActivity(tourEntry({ tour: created, action: 'start', user, time: nowSeconds() }))
					openTour()
				})}
			/>
		)
	}
	else if (tourScreen === 'pitch' && tour && tourPitch && pitches)
	{
		content = (
			<FieldTourPitch
				key={tourPitch.pitch.location}
				tour={tour}
				pitch={findPitchByLocation(pitches, tourPitch.pitch.location) || tourPitch.pitch}
				initialReport={tourPitch.report === true}
				onReportChange={(open) => setTourPitch(current => (current && (current.report === true) !== open ? { ...current, report: open } : current))}
				pitches={pitches}
				user={user}
				withInventory={tourWithInventory()}
				beforeIds={tourPitch.beforeIds}
				toast={tourToast}
				onToast={setTourToast}
				onDismissToast={dismissTourToast}
				onChangeTour={changeTour}
				onOpenIntervention={openIntervention}
				onOpenInventory={() => {
					setTourToast(null)
					setInventory({ type: 'tour', pitch: tourPitch.pitch, stay: null, client: null, tour, startedAt: nowSeconds() })
				}}
				onBack={() => {
					setTourToast(null)
					setTourPitch(null)
					setTourScreen('plan')
				}}
				onNext={(next) => openPitch(next, false)}
			/>
		)
	}
	else if (tourScreen && tour && tour.status === 'open')
	{
		content = (
			<FieldTourPlan
				tour={tour}
				pitches={pitches}
				loadError={pitchesError}
				onRetry={loadPitches}
				state={planState}
				onState={setPlanState}
				onPick={(pitch) => openPitch(pitch, true)}
				onBack={goHome}
			/>
		)
	}
	else if (flow)
	{
		content = (
			<FieldStart
				type={flow}
				onExit={goHome}
				onDone={(context) => {
					setFlow(null)
					setInventory(context)
				}}
				scrollToTop={scrollToTop}
			/>
		)
	}
	else
	{
		content = (
			<React.Fragment>
				<UndoToast
					toast={toast}
					onDismiss={dismissToast}
					onUndo={() => {
						const current = toast
						setToast(null)
						Promise.resolve(current.undo()).catch(() => null)
					}}
				/>
				<FieldHeader showDashboard={showDashboard} onDashboard={goToDashboard} />
				<FieldHome
					user={user}
					tour={visibleTour}
					hiddenTour={hiddenTour}
					pitches={pitches}
					canWrite={writeAllowed}
					canStartTour={canStartTour(user)}
					onStart={start}
					onResumeTour={resumeTour}
					onTourOptions={() => setSheet('options')}
					onActivity={openActivity}
				/>
				{(sheet === 'options' || sheet === 'reminder') && tour && (
					<TourOptionsSheet
						tour={tour}
						user={user}
						reminder={sheet === 'reminder'}
						onShare={() => setSheet('share')}
						onEnd={endTour}
						onHide={hideTour}
						onKeep={keepTour}
						onClose={() => setSheet(null)}
					/>
				)}
				{sheet === 'share' && tour && <TourShareSheet tour={tour} pitches={pitches} onClose={() => setSheet(null)} />}
			</React.Fragment>
		)
	}

	return (
		<div ref={scrollRef} className="field-app" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, overflowY: 'auto', overscrollBehavior: 'none', WebkitOverflowScrolling: 'touch', background: colors.background, color: colors.text, fontFamily, fontSize: 18, lineHeight: 1.45 }}>
			<div style={{ maxWidth: 576, margin: '0 auto', minHeight: '100%', display: 'flex', flexDirection: 'column', paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
				{content}
			</div>
			{(outbox.pending > 0 || outbox.failed > 0) && <div role="status" style={{ position: 'fixed', left: 0, right: 0, top: 'max(8px, env(safe-area-inset-top))', display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 30 }}><span style={{ background: outbox.failed > 0 ? '#B91C1C' : '#B45309', color: '#FFFFFF', borderRadius: 999, padding: '8px 16px', fontSize: 14, fontWeight: 600 }}>{outbox.failed > 0 ? plural(outbox.failed, 'envoi refusé par le serveur', 'envois refusés par le serveur') : plural(outbox.pending, 'envoi en attente', 'envois en attente') + (navigator.onLine === false ? ' : pas de réseau' : ' : nouvel essai en cours' + (outbox.reason ? ' (' + outbox.reason + ')' : ''))}</span></div>}
			{pitchCheckingSlow && <div role="status" style={{ position: 'fixed', left: 0, right: 0, bottom: 'max(24px, env(safe-area-inset-bottom))', display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 30 }}><span style={{ background: colors.text, color: '#FFFFFF', borderRadius: 999, padding: '10px 18px', fontSize: 15, fontWeight: 600 }}>Vérification de l'emplacement…</span></div>}
		</div>
	)
}
